import { c as _c } from "react/compiler-runtime";
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ErrorBoundary from '@sbt-web/error-boundary';
import { OptimizelySubitoContext } from '@sbt-web/houston-wrapper';
import { getConversationID, getChannelInfo } from '@sbt-web/network/client';
import { ItemID, CategoryID, NetError, HTTPStatusCode } from '@sbt-web/network/types';
import { urnTransformer } from '@sbt-web/utils';
import { getAdvertiserName, isPro } from '@/shared/models/Advertiser';
import { sendNotification } from '@/tools/Notifications';
import { UsernameDialogAction, UsernameDialogEvent } from '../UsernameDialog';
import { ContactForm } from './components/ContactForm';
import { AdReplyEvent } from './events';
import { trackContactIntention } from './tracking';
import type { AdReplyProps } from './types';
import { getSearchValue, isUserEligibleForStream as isUserEligibleForStreamUtil } from './utils';
const AdReplyComponent = ({
  isMobile,
  userId,
  requestLogin,
  onSendReplyCallback,
  onStatusDialogChanged,
  item,
  advertiserProfile,
  shop
}: AdReplyProps) => {
  const {
    urn,
    subject,
    category,
    advertiser
  } = item;
  const advertiserId = advertiser.userId;
  const categoryId = category.id;
  const conversationURL = useRef<string | undefined>(undefined);
  const [buyerName, setBuyerName] = useState<string | undefined>(undefined);
  const [isUserEligibleForStream, setIsUserEligibleForStream] = useState<boolean | undefined>();
  const [showContactForm, setShowContactForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const adReplyWrapperElement = useRef<HTMLDivElement>(null);
  const searchValue = getSearchValue();
  const enableAttachments = categoryId === CategoryID.OfferteLavoro;
  const isAProAdvertiser = isPro(advertiser.type);
  const advertiserName = getAdvertiserName(advertiser.type, shop, advertiserProfile);
  const {
    optimizely
  } = useContext(OptimizelySubitoContext);
  useEffect(() => {
    if (!userId) return;
    const checkIfUserIsEligibleForStream = async () => {
      const isEligible = await isUserEligibleForStreamUtil(userId, optimizely);
      setIsUserEligibleForStream(isEligible);
    };
    checkIfUserIsEligibleForStream();
  }, [optimizely, userId]);
  useEffect(() => {
    //Ensure the Houston toggle value is retrieved before making any calls to the messaging or stream endpoints.
    if (!userId || isUserEligibleForStream === undefined) return;
    const getURLOfExistingConversation = async () => {
      const itemIdInfo = urnTransformer(urn);
      if (!itemIdInfo) return;
      const itemIdForMessaging: ItemID = `ad${itemIdInfo?.adId}list${itemIdInfo?.contentId}`;
      try {
        const response = await getConversationID(process.env.NEXT_PUBLIC_HERMES_BASE_URL, userId, advertiserId, itemIdForMessaging);
        if (response.body) {
          conversationURL.current = `${process.env.NEXT_PUBLIC_AREARISERVATA_BASE_URL}/messaggi#/id/${response.body}`;
        }
      } catch {
        return;
      }
    };
    if (!isUserEligibleForStream) {
      getURLOfExistingConversation();
    }
  }, [advertiserId, urn, userId, isUserEligibleForStream]);
  const handleShowContactForm = useCallback(() => {
    if (conversationURL.current) return window.location.assign(conversationURL.current);
    window.dispatchEvent(new CustomEvent('subito:AdReply:Show'));
    setShowContactForm(true);
  }, []);
  useEffect(() => {
    if (!showContactForm) return;
    if (!isMobile && adReplyWrapperElement.current) {
      window.scrollTo({
        top: adReplyWrapperElement.current.offsetTop,
        behavior: 'smooth'
      });
    }
  }, [isMobile, showContactForm]);
  const handleCheckBuyerName = (userId_0: string) => {
    window.dispatchEvent(new CustomEvent(UsernameDialogEvent.CheckBuyerName, {
      detail: {
        action: UsernameDialogAction.Contact,
        userId: userId_0
      }
    }));
  };
  const handleGetBuyerName: EventListener = useCallback(event => {
    if (event instanceof CustomEvent && event.detail?.action === UsernameDialogAction.Contact) {
      setBuyerName(event.detail.buyerName);
      handleShowContactForm();
    }
  }, [handleShowContactForm]);
  const handleNextStep = useCallback((userId_1: string) => buyerName ? handleShowContactForm() : handleCheckBuyerName(userId_1), [buyerName, handleShowContactForm]);
  const openLoginInPlace = useCallback(async () => {
    const {
      id
    } = await requestLogin();
    if (id !== advertiserId) {
      handleNextStep(id);
    }
  }, [advertiserId, handleNextStep, requestLogin]);
  const getChannelIdFromStream = useCallback(async () => {
    const itemIdInfo_0 = urnTransformer(urn);
    if (!itemIdInfo_0) return;
    setIsLoading(true);
    try {
      const {
        body
      } = await getChannelInfo(process.env.NEXT_PUBLIC_HADES_BASE_URL, userId, itemIdInfo_0?.adId, advertiserId);
      conversationURL.current = `${process.env.NEXT_PUBLIC_AREARISERVATA_BASE_URL}/messaggi-chat?channel_id=${body.channel.id}`;
    } catch (e) {
      if (e instanceof NetError && e.cause.status === HTTPStatusCode.NotFound) {
        return;
      }
      sendNotification('Si è verificato un problema', 'alert');
    } finally {
      setIsLoading(false);
    }
  }, [advertiserId, userId, urn]);
  const handleContactButtonClick = useCallback(async () => {
    if (isLoading) return;
    trackContactIntention(item);
    if (!userId) return openLoginInPlace();
    if (isUserEligibleForStream) {
      await getChannelIdFromStream();
    }
    handleNextStep(userId);
  }, [handleNextStep, item, userId, openLoginInPlace, getChannelIdFromStream, isUserEligibleForStream, isLoading]);
  useEffect(() => {
    window.addEventListener(UsernameDialogEvent.GetBuyerName, handleGetBuyerName);
    return () => {
      window.removeEventListener(UsernameDialogEvent.GetBuyerName, handleGetBuyerName);
    };
  }, [handleGetBuyerName]);
  useEffect(() => {
    window.addEventListener(AdReplyEvent.ContactButtonClick, handleContactButtonClick);
    const closeContactForm = () => setShowContactForm(false);
    window.addEventListener(AdReplyEvent.Close, closeContactForm);
    return () => {
      window.removeEventListener(AdReplyEvent.ContactButtonClick, handleContactButtonClick);
      window.removeEventListener(AdReplyEvent.Close, closeContactForm);
    };
  }, [handleContactButtonClick]);
  useEffect(() => {
    onStatusDialogChanged?.(showContactForm);
  }, [showContactForm, onStatusDialogChanged]);
  const handleCloseContactForm = () => {
    window.dispatchEvent(new CustomEvent(AdReplyEvent.Cancel));
    setShowContactForm(false);
  };
  return <div ref={adReplyWrapperElement}>
      {showContactForm && <ContactForm onCloseContactForm={handleCloseContactForm} isMobile={isMobile} category={categoryId} enableAttachments={enableAttachments} isPro={isAProAdvertiser} userId={userId} advertiserName={advertiserName} buyerName={buyerName} subject={subject} adUrn={urn} onSendReplyCallback={onSendReplyCallback} requestLogin={requestLogin} isModalOpened={showContactForm} search={searchValue} item={item} />}
    </div>;
};
export const AdReply = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <ErrorBoundary><AdReplyComponent {...props} /></ErrorBoundary>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};